<template>
    <!-- Toolbar — Bedienleiste (Variante B): links Ansicht (Titel + Klapp-Schalter,
         der die ganze Liste steuert), rechts Aktionen (Hauptaktion + Überlauf).
         Selten Gebrauchtes („Ebenen benennen") liegt im ⋯-Menü, damit die Leiste
         ruhig bleibt und später mitwachsen kann. -->
    <div class="flex justify-between items-center flex-wrap gap-3 mt-6">
        <!-- Links: Auf-/Zuklappen der ganzen Liste + Titel.
             Pfeil LINKS vor dem Titel und derselbe Einzelpfeil wie bei den Gruppen
             (runter = offen, nach rechts = zu) — damit die Klapp-Logik einheitlich ist. -->
        <div class="flex items-center gap-1.5">
            <button v-if="hasBuildings" type="button" @click="toggleAll"
                    class="inline-flex items-center justify-center w-7 h-7 -ml-1 rounded-lg text-minplan-grey hover:bg-minplan-lightwhite hover:text-minplan-blue transition-colors"
                    :title="anyExpanded() ? $t('buildings.collapseAll') : $t('buildings.expandAll')"
                    :aria-label="anyExpanded() ? $t('buildings.collapseAll') : $t('buildings.expandAll')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                     class="w-5 h-5 transition-transform" :class="{ '-rotate-90': !anyExpanded() }">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            <div class="font-bold text-xl">{{ $t("projects.apartments") }}</div>
        </div>

        <!-- Rechts: Hauptaktion + Überlauf-Menü -->
        <div class="flex items-center gap-2 flex-wrap">
            <button type="button" @click="openCreate"
                    class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-minplan-blue text-white font-semibold text-sm hover:opacity-90 transition-opacity">
                <span class="text-lg leading-none">+</span> {{ $t("projects.newApartment") }}
            </button>
            <!-- ⋯ nur mit Gebäuden — dann existiert „Ebenen benennen" (Thema 18:
                 die Namen werden zu den Spaltenüberschriften der Exporte). -->
            <div v-if="hasBuildings" class="relative">
                <button type="button" @click="toolMenuOpen = !toolMenuOpen"
                        class="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white text-minplan-grey border border-gray-200 hover:bg-minplan-lightwhite hover:text-minplan-blue transition-colors"
                        :aria-label="$t('buildings.menu')" :title="$t('buildings.menu')">
                    <svg viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                        <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
                    </svg>
                </button>
                <div v-if="toolMenuOpen"
                     class="absolute right-0 top-11 z-30 min-w-[210px] p-1.5 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <button type="button" @click="openLevelNames(); toolMenuOpen = false"
                            class="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-sm text-minplan-blue text-left hover:bg-minplan-lightwhite">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 flex-shrink-0 text-minplan-grey">
                            <path d="M4 21V5a1 1 0 011-1h9a1 1 0 011 1v16M15 9h4a1 1 0 011 1v11" />
                        </svg>
                        {{ $t("buildings.levelNames") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <!-- Klick außerhalb schließt das Überlauf-Menü -->
    <div v-if="toolMenuOpen" class="fixed inset-0 z-20" @click="toolMenuOpen = false"></div>

    <!-- Ohne Gebäude → flache Liste -->
    <template v-if="!hasBuildings">
        <ul role="list" class="mt-2">
            <li v-for="apartment in flat" :key="apartment.uuid" class="py-2">
                <projects-apartments-card :apartment="apartment" :project="project" />
            </li>
        </ul>
        <div v-if="addingParent === '__root__'"
             class="flex items-center px-3.5 py-3 border border-dashed border-gray-300 rounded-lg mt-2">
            <input class="w-full text-sm text-minplan-blue border border-gray-300 rounded px-2.5 py-2"
                   :placeholder="$t('buildings.nameOf', { level: levelName(1) })" :ref="setAddRef"
                   @keyup.enter="commitAdd($event.target.value)" @keyup.esc="cancelAdd"
                   @blur="commitAdd($event.target.value)" />
        </div>
        <div v-else @click="startAddRoot"
             class="flex items-center gap-2 px-3.5 py-3 border border-dashed border-gray-300 rounded-lg text-minplan-grey text-sm cursor-pointer hover:bg-minplan-lightwhite mt-2">
            <span class="text-lg leading-none">+</span> {{ $t("buildings.addNamed", { level: levelName(1) }) }}
        </div>
    </template>

    <template v-else>
        <div v-if="unassigned.length"
             class="flex items-center gap-2 mt-4 px-3.5 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <span class="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
            <span>{{ unassigned.length === 1
                ? $t("buildings.nudgeOne")
                : $t("buildings.nudgeMany", { count: unassigned.length }) }}</span>
        </div>

        <div class="mt-3 flex flex-col gap-3">
            <projects-building-group v-if="unassigned.length"
                :node="unassignedNode" :parent-scope="null" :level="1" :unassigned="true" />

            <projects-building-group v-for="root in rootBuildings" :key="root.uuid"
                :node="root" :parent-scope="'__root__'" :level="1" />

            <div v-if="addingParent === '__root__'"
                 class="flex items-center px-3.5 py-3 border border-dashed border-gray-300 rounded-lg">
                <input class="w-full text-sm text-minplan-blue border border-gray-300 rounded px-2.5 py-2"
                       :placeholder="$t('buildings.nameOf', { level: levelName(1) })" :ref="setAddRef"
                       @keyup.enter="commitAdd($event.target.value)" @keyup.esc="cancelAdd"
                       @blur="commitAdd($event.target.value)" />
            </div>
            <div v-else @click="startAddRoot"
                 class="flex items-center gap-2 px-3.5 py-3 border border-dashed border-gray-300 rounded-lg text-minplan-grey text-sm cursor-pointer hover:bg-minplan-lightwhite">
                <span class="text-lg leading-none">+</span> {{ $t("buildings.addNamed", { level: levelName(1) }) }}
            </div>
        </div>
    </template>

    <div v-if="openMenu" class="fixed inset-0 z-20" @click="closeMenu"></div>

    <!-- Entscheid 2.1/2.3: „Verschieben nach…" — für Untergruppen und Wohnungen,
         damit beides auch ohne Maus und auf Touch-Geräten funktioniert. -->
    <jet-dialog-modal :show="!!moving" @close="cancelMove">
        <template #title>{{ $t("buildings.moveTitle") }}</template>
        <template #content>
            <p class="text-sm text-minplan-grey mb-3">
                {{ $t("buildings.moveIntro", { name: moving?.name }) }}
            </p>
            <ul role="list" class="max-h-72 overflow-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                <li v-for="option in moveTargets" :key="option.key">
                    <button type="button" class="w-full text-left px-3 py-2.5 text-sm hover:bg-minplan-lightwhite disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                            :disabled="option.disabled" @click="doMove(option)">
                        <span :style="{ paddingLeft: option.indent + 'px' }"
                              :class="option.disabled ? 'text-minplan-grey' : 'text-minplan-blue'">{{ option.label }}</span>
                        <span v-if="option.hint" class="text-xs text-minplan-grey"> — {{ option.hint }}</span>
                    </button>
                </li>
            </ul>

            <!-- Befund B-1: der Server weist ein unzulässiges Ziel ab, die
                 Meldung stand bisher nur in `$page.props.errors` und wurde
                 nirgends gezeigt. Darstellung wie in `Modals/Archive.vue`. -->
            <p v-if="moveError" data-test="move-error" class="text-xs text-red-600 mt-2">
                {{ moveError }}
            </p>
        </template>
        <template #footer>
            <jet-secondary-button @click="cancelMove">{{ $t("actions.cancel") }}</jet-secondary-button>
        </template>
    </jet-dialog-modal>

    <!-- Thema 18: Ebenen benennen -->
    <jet-dialog-modal :show="editingLevels" @close="editingLevels = false">
        <template #title>{{ $t("buildings.levelNamesTitle") }}</template>
        <template #content>
            <p class="text-sm text-minplan-grey mb-4">{{ $t("buildings.levelNamesIntro") }}</p>
            <div class="space-y-3">
                <div v-for="(entwurf, index) in levelDraft" :key="index" class="flex items-center gap-3">
                    <span class="w-20 flex-shrink-0 text-sm text-minplan-grey">
                        {{ $t("buildings.level", { number: index + 1 }) }}
                    </span>
                    <input type="text" v-model="levelDraft[index]" maxlength="40"
                           :placeholder="(levelNameDefaults || [])[index]"
                           class="flex-1 min-w-0 text-sm text-minplan-blue border border-gray-300 rounded px-2.5 py-2" />
                </div>
            </div>
        </template>
        <template #footer>
            <jet-secondary-button @click="editingLevels = false">{{ $t("actions.cancel") }}</jet-secondary-button>
            <jet-primary-button class="ml-2" @click="saveLevelNames">{{ $t("actions.save") }}</jet-primary-button>
        </template>
    </jet-dialog-modal>

    <jet-confirmation-modal :show="!!pendingDelete" @close="pendingDelete = null">
        <template #title>{{ $t("buildings.deleteTitle") }}</template>
        <template #content>{{ deleteMessage }}</template>
        <template #footer>
            <jet-secondary-button @click="pendingDelete = null">{{ $t("actions.cancel") }}</jet-secondary-button>
            <jet-danger-button class="ml-2" @click="doDelete">{{ $t("buildings.delete") }}</jet-danger-button>
        </template>
    </jet-confirmation-modal>
</template>

<script>
import ProjectsApartmentsCard from "@/Shared/Components/Projects/ProjectsApartmentsCard";
import ProjectsBuildingGroup from "@/Shared/Components/Projects/ProjectsBuildingGroup";
import JetConfirmationModal from "@/Jetstream/ConfirmationModal.vue";
import JetDialogModal from "@/Jetstream/DialogModal.vue";
import JetDangerButton from "@/Jetstream/DangerButton.vue";
import JetPrimaryButton from "@/Jetstream/PrimaryButton.vue";
import JetSecondaryButton from "@/Jetstream/SecondaryButton.vue";

// Thema 15/16/17 — Gebäude-Gruppierung mit Untergruppen bis Ebene 4 und freier
// Anordnung (Wohnungen + Untergruppen teilen eine Reihenfolge je Ebene). Kein
// Verwalten-Modus. Container hält den Zustand und stellt ihn den rekursiven
// ProjectsBuildingGroup-Komponenten via provide (groupCtx) bereit.
//
// Drag-and-drop ist natives HTML5-DnD und damit Maus/Desktop. Das ist bewusst so:
// statt SortableJS gibt es „Verschieben nach…" (Entscheid 2.3), das auf jedem Gerät
// und ohne Maus funktioniert. Drag bleibt die Abkürzung am Schreibtisch.
export default {
    props: ["project", "apartments", "buildings", "levelNames", "levelNameDefaults", "levelNamesCustom"],
    components: {
        ProjectsApartmentsCard, ProjectsBuildingGroup,
        JetConfirmationModal, JetDialogModal,
        JetDangerButton, JetPrimaryButton, JetSecondaryButton,
    },
    data() {
        return {
            collapsed: {},
            toolMenuOpen: false, // Überlauf-Menü der Bedienleiste (Variante B)
            moving: null,       // { type: 'building'|'apartment', uuid, name }
            // Befund B-1: solange wahr, gilt eine Meldung in
            // `$page.props.errors.target` nicht dem laufenden Versuch —
            // dieselbe Bauart wie `errorDismissed` in `Modals/Archive.vue`.
            moveErrorDismissed: true,
            editingLevels: false,
            levelDraft: [],
            dragged: null,
            dragOver: { uuid: null, before: true },
            dragOverInto: null,   // Key des Knotens, in den gerade „hinein" gezogen wird
            editing: null,
            openMenu: null,
            addingParent: null,
            pendingDelete: null,
        };
    },
    provide() {
        return {
            groupCtx: {
                // Befund H-5: als Funktion, nicht als Wert — alle Gebaeude-
                // Mutationen fahren `preserveState: true`, Inertia ersetzt
                // die Props, und eine Momentaufnahme zeigte danach weiter
                // aufs alte Projekt.
                project: () => this.project,
                apartmentsIn: this.apartmentsIn,
                apartmentByUuid: this.apartmentByUuid,
                totalCount: this.totalCount,
                itemsOf: this.itemsOf,
                itemPos: this.itemPos,
                itemLen: this.itemLen,
                isCollapsed: this.isCollapsed,
                toggle: this.toggle,
                editingUuid: () => this.editing,
                openMenuUuid: () => this.openMenu,
                addingParentUuid: () => this.addingParent,
                draggingUuid: () => (this.dragged ? this.dragged.uuid : null),
                dropClass: this.dropClass,
                intoKey: () => this.dragOverInto,
                toggleMenu: this.toggleMenu,
                closeMenu: this.closeMenu,
                startRename: this.startRename,
                commitRename: this.commitRename,
                setEditRef: this.setEditRef,
                startAddUnder: this.startAddUnder,
                commitAdd: this.commitAdd,
                cancelAdd: this.cancelAdd,
                setAddRef: this.setAddRef,
                moveGroupStep: this.moveGroupStep,
                requestMove: this.requestMove,
                canAddSubgroup: this.canAddSubgroup,
                levelName: this.levelName,
                maxDepth: () => this.maxDepth,
                requestDelete: this.requestDelete,
                onDragStart: this.onDragStart,
                onDragEnd: this.onDragEnd,
                onItemOver: this.onItemOver,
                onItemDrop: this.onItemDrop,
                onIntoOver: this.onIntoOver,
                onIntoDrop: this.onIntoDrop,
            },
        };
    },
    computed: {
        hasBuildings() {
            return (this.buildings || []).length > 0;
        },
        /** Entscheid 2.5: Die Grenze steht am Server; levelNames hat genau so viele Einträge. */
        maxDepth() {
            return (this.levelNames || []).length;
        },
        rootBuildings() {
            return [...(this.buildings || [])].sort((a, b) => a.position - b.position);
        },
        rootUuids() {
            return this.rootBuildings.map((b) => b.uuid);
        },
        unassignedNode() {
            return { uuid: null, name: this.$t("buildings.unassigned"), children: [] };
        },
        flat() {
            return [...(this.apartments || [])].sort(this.byNaturalName);
        },
        unassigned() {
            return this.apartmentsIn(null);
        },
        deleteMessage() {
            if (!this.pendingDelete) return "";
            const count = this.totalCount(this.pendingDelete);
            const name = this.pendingDelete.name;
            return count > 0
                ? this.$t("buildings.deleteConfirmWithApartments", { name, count })
                : this.$t("buildings.deleteConfirm", { name });
        },

        /**
         * Entscheid 2.2: Aufklapp-Zustand pro Nutzer und Projekt im Browser.
         * Der Nutzer steckt im Schlüssel, damit zwei Konten am selben Rechner
         * sich nicht gegenseitig den Zustand überschreiben.
         */
        storageKey() {
            // Befund H-4: `user.uuid` gibt es im Shared-Prop nicht —
            // `HandleInertiaRequests::makeUserViewModel()` liefert
            // `advisor_uuid`/`id`. Der Ausdruck fiel bisher immer auf "anon"
            // zurueck, und zwei Konten am selben Rechner ueberschrieben sich
            // gegenseitig den Aufklapp-Zustand.
            const user =
                this.$page.props.user?.advisor_uuid ?? this.$page.props.user?.id ?? "anon";
            return `immivo.buildings.collapsed.${user}.${this.project.uuid}`;
        },

        /**
         * Alle Gebäude einmal flach, in Anzeige-Reihenfolge: Knoten, Ebene (1-basiert)
         * und der Scope des Elternteils. Eine Quelle für Suchen, Elternbestimmung,
         * Aufklapp-Schlüssel und die Zielliste des Verschieben-Dialogs — vorher lief
         * dafür je eine eigene Rekursion durch denselben Baum.
         */
        flatNodes() {
            const out = [];
            const walk = (nodes, level, parentScope) => {
                [...(nodes || [])]
                    .sort((a, b) => a.position - b.position)
                    .forEach((node) => {
                        out.push({ node, uuid: node.uuid, level, parentScope });
                        walk(node.children, level + 1, node.uuid);
                    });
            };
            walk(this.rootBuildings, 1, "__root__");

            return out;
        },

        /**
         * Entscheid 2.1/2.3: mögliche Ziele des Verschieben-Dialogs, hierarchisch
         * eingerückt. Unmögliche Ziele bleiben sichtbar, aber deaktiviert und mit
         * Begründung — sonst rätselt der Betreuer, warum ein Gebäude fehlt.
         */
        /**
         * Befund B-1: die Begründung des Servers für ein abgewiesenes Ziel.
         * Der Weg über die geteilte `errors`-Prop ist derselbe wie in
         * `Pages/Projects/Modals/Archive.vue`; `moveErrorDismissed` sorgt
         * dafür, dass eine ältere Meldung nicht als Antwort auf einen neuen
         * Versuch gelesen wird.
         */
        moveError() {
            if (this.moveErrorDismissed) return null;

            const raw = this.$page.props.errors?.target;
            if (!raw) return null;

            return Array.isArray(raw) ? raw[0] : raw;
        },

        moveTargets() {
            if (!this.moving) return [];

            const isBuilding = this.moving.type === "building";
            const node = isBuilding ? this.findNode(this.moving.uuid) : null;
            const blocked = node ? [this.moving.uuid, ...this.descendantUuids(node)] : [];
            // Nur Gruppen belegen Ebenen — eine Wohnung darf auch in ein Gebäude
            // auf der untersten Ebene, ihr Teilbaum ist gedanklich null hoch.
            const ownHeight = node ? this.nodeHeight(node) : 0;
            const currentParent = isBuilding
                ? this.parentScopeOf(this.moving.uuid)
                : (this.apartmentByUuid(this.moving.uuid)?.building_uuid || null);

            const reasonFor = (uuid, level) => {
                if (blocked.includes(uuid)) return "moveOwnSubtree";
                if (level + ownHeight > this.maxDepth) return "moveTooDeep";
                if (uuid === currentParent) return "moveCurrent";
                return null;
            };
            const option = (key, target, label, level, reason) => ({
                key,
                target,
                label,
                indent: (level - 1) * 16,
                disabled: !!reason,
                hint: reason ? this.$t(`buildings.${reason}`) : "",
            });

            return [
                // Nur Untergruppen dürfen auf die Wurzelebene — eine Wohnung soll laut
                // Entscheid 2.4 nicht absichtlich in „Noch nicht zugeordnet" wandern.
                ...(isBuilding
                    ? [option(
                        "__root__",
                        null,
                        this.$t("buildings.rootLevel"),
                        1,
                        currentParent === "__root__" ? "moveCurrent" : null,
                    )]
                    : []),
                ...this.flatNodes.map(({ node: candidate, uuid, level }) =>
                    option(uuid, uuid, candidate.name, level, reasonFor(uuid, level)),
                ),
            ];
        },
    },
    created() {
        this.collapsed = this.loadCollapsed();
    },
    watch: {
        collapsed: {
            deep: true,
            handler(value) {
                this.saveCollapsed(value);
            },
        },
    },
    methods: {
        byNaturalName(a, b) {
            return String(a.name).localeCompare(String(b.name), undefined, { numeric: true });
        },
        apartmentsIn(buildingUuid) {
            return (this.apartments || [])
                .filter((a) => (a.building_uuid || null) === buildingUuid)
                .sort((a, b) => (a.building_position - b.building_position) || this.byNaturalName(a, b));
        },
        apartmentByUuid(uuid) {
            return (this.apartments || []).find((a) => a.uuid === uuid) || null;
        },
        findNode(uuid) {
            return this.flatNodes.find((entry) => entry.uuid === uuid)?.node || null;
        },
        // Gemeinsame, sortierte Elemente (Wohnungen + Untergruppen) einer Ebene
        itemsOf(scope) {
            if (scope === null) {
                return this.apartmentsIn(null).map((a) => ({ type: "apartment", uuid: a.uuid, pos: a.building_position }));
            }
            if (scope === "__root__") {
                return this.rootBuildings.map((b) => ({ type: "building", uuid: b.uuid, pos: b.position }));
            }
            const node = this.findNode(scope);
            if (!node) return [];
            const apts = this.apartmentsIn(scope).map((a) => ({ type: "apartment", uuid: a.uuid, pos: a.building_position }));
            const subs = (node.children || []).map((b) => ({ type: "building", uuid: b.uuid, pos: b.position }));
            return [...apts, ...subs].sort((x, y) => (x.pos - y.pos) || (x.type === "apartment" ? -1 : 1));
        },
        itemPos(scope, uuid) {
            return this.itemsOf(scope).findIndex((it) => it.uuid === uuid);
        },
        itemLen(scope) {
            return this.itemsOf(scope).length;
        },
        totalCount(node) {
            let n = this.apartmentsIn(node.uuid || null).length;
            (node.children || []).forEach((c) => (n += this.totalCount(c)));
            return n;
        },
        allKeys() {
            const keys = this.flatNodes.map((entry) => entry.uuid);
            if (this.unassigned.length) keys.push("__unassigned__");
            return keys;
        },
        isCollapsed(key) {
            return !!this.collapsed[key];
        },
        toggle(key) {
            this.collapsed = { ...this.collapsed, [key]: !this.collapsed[key] };
        },
        anyExpanded() {
            return this.allKeys().some((k) => !this.collapsed[k]);
        },
        toggleAll() {
            const collapse = this.anyExpanded();
            const map = {};
            this.allKeys().forEach((k) => (map[k] = collapse));
            this.collapsed = map;
        },

        openCreate() {
            this.$inertia.visitInModal(
                this.route("advisors.apartments.create", { project: this.project.uuid }),
            );
        },

        toggleMenu(uuid) {
            this.openMenu = this.openMenu === uuid ? null : uuid;
        },
        closeMenu() {
            this.openMenu = null;
        },
        setEditRef(el) {
            if (el) this.$nextTick(() => { el.focus(); el.select(); });
        },
        setAddRef(el) {
            if (el) this.$nextTick(() => el.focus());
        },

        startAddRoot() {
            this.addingParent = "__root__";
        },
        startAddUnder(parentUuid) {
            this.closeMenu();
            this.addingParent = parentUuid;
        },
        cancelAdd() {
            this.addingParent = null;
        },
        commitAdd(name) {
            // Doppel-Anlegen verhindern: Enter legt an und entfernt das Eingabefeld,
            // dessen blur würde commitAdd sonst ein zweites Mal auslösen (→ Gebäude
            // doppelt). Auch cancelAdd/Esc setzt addingParent=null; danach darf ein
            // nachfolgendes blur nichts mehr anlegen. Ist kein Eingabemodus aktiv
            // (addingParent === null), sofort abbrechen.
            if (this.addingParent === null) return;
            const clean = (name || "").trim();
            const parent = this.addingParent === "__root__" ? null : this.addingParent;
            this.addingParent = null;
            if (!clean) return;
            this.$inertia.post(
                this.route("advisors.projects.buildings.store", { project: this.project.uuid }),
                { name: clean, parent },
                { preserveScroll: true, preserveState: true },
            );
        },

        startRename(node) {
            this.closeMenu();
            this.editing = node.uuid;
        },
        commitRename(node, name) {
            if (this.editing !== node.uuid) return;
            this.editing = null;
            const clean = (name || "").trim();
            if (!clean || clean === node.name) return;
            this.$inertia.put(
                this.route("advisors.projects.buildings.update", {
                    project: this.project.uuid, building: node.uuid,
                }),
                { name: clean },
                { preserveScroll: true, preserveState: true },
            );
        },

        // ⋯ Nach oben/unten: Wurzel-Gebäude via reorder, sonst über die gemeinsame Reihenfolge
        moveGroupStep(parentScope, uuid, dir) {
            this.closeMenu();
            if (parentScope === "__root__") {
                const i = this.rootUuids.indexOf(uuid);
                const order = [...this.rootUuids];
                const j = i + dir;
                if (j < 0 || j >= order.length) return;
                [order[i], order[j]] = [order[j], order[i]];
                this.$inertia.put(
                    this.route("advisors.projects.buildings.reorder", { project: this.project.uuid }),
                    { buildings: order },
                    { preserveScroll: true, preserveState: true },
                );
                return;
            }
            const items = this.itemsOf(parentScope);
            const i = items.findIndex((it) => it.uuid === uuid);
            const j = i + dir;
            if (j < 0 || j >= items.length) return;
            [items[i], items[j]] = [items[j], items[i]];
            this.arrange(parentScope, items);
        },

        // Entscheid 2.2 — Aufklapp-Zustand im Browser
        loadCollapsed() {
            try {
                const raw = window.localStorage.getItem(this.storageKey);
                const parsed = raw ? JSON.parse(raw) : null;
                // Gebäude, die es nicht mehr gibt, dürfen im Zustand verwaisen —
                // sie stören nicht, weil nur nach Schlüssel gefragt wird.
                return parsed && typeof parsed === "object" ? parsed : {};
            } catch (e) {
                return {};
            }
        },
        saveCollapsed(value) {
            try {
                window.localStorage.setItem(this.storageKey, JSON.stringify(value || {}));
            } catch (e) {
                /* Privater Modus oder volles Kontingent — der Zustand ist dann nur flüchtig. */
            }
        },

        // Entscheid 2.1/2.3 — Verschieben über den Dialog
        nodeHeight(node) {
            const children = node.children || [];
            return children.length
                ? 1 + Math.max(...children.map((child) => this.nodeHeight(child)))
                : 1;
        },
        descendantUuids(node) {
            return (node.children || []).flatMap(
                (child) => [child.uuid, ...this.descendantUuids(child)],
            );
        },
        /** '__root__' oder die UUID der Eltern-Gruppe. */
        parentScopeOf(uuid) {
            return this.flatNodes.find((entry) => entry.uuid === uuid)?.parentScope || null;
        },
        /** Entscheid 2.5: „Untergruppe" entfällt, wenn die letzte Ebene erreicht ist. */
        canAddSubgroup(level) {
            return level < this.maxDepth;
        },

        // Thema 18 — benannte Ebenen
        /** Name einer Ebene, 1-basiert. Der Server liefert sie bereits aufgelöst. */
        levelName(level) {
            return (this.levelNames || [])[level - 1] || "";
        },
        openLevelNames() {
            // Nur das tatsächlich Gespeicherte, nicht die aufgelösten Namen — sonst
            // schriebe blosses Öffnen und Speichern die Vorbelegung fest, und wer eine
            // Ebene bewusst „Gebäude" nennt, fände das Feld leer.
            this.levelDraft = (this.levelNameDefaults || []).map(
                (unused, index) => (this.levelNamesCustom || [])[index] || "",
            );
            this.editingLevels = true;
        },
        saveLevelNames() {
            const levels = [...this.levelDraft];
            this.editingLevels = false;
            this.$inertia.put(
                this.route("advisors.projects.levels.update", { project: this.project.uuid }),
                { levels },
                { preserveScroll: true, preserveState: true },
            );
        },
        requestMove(type, uuid, name) {
            this.closeMenu();
            this.moving = { type, uuid, name };
            this.moveErrorDismissed = true;
        },
        cancelMove() {
            this.moving = null;
            this.moveErrorDismissed = true;
        },
        doMove(option) {
            if (option.disabled || !this.moving) return;
            const { type, uuid } = this.moving;

            if (type === "building") {
                // Befund B-1: der Dialog bleibt bis zur Antwort stehen. Weist
                // der Server das Ziel ab (veralteter Baum im Client), trägt er
                // die Begründung — vorher schloss er sofort und die Meldung
                // hatte keinen Ort mehr.
                this.moveErrorDismissed = true;
                this.$inertia.put(
                    this.route("advisors.projects.buildings.move", {
                        project: this.project.uuid, building: uuid,
                    }),
                    { target: option.target },
                    {
                        preserveScroll: true,
                        preserveState: true,
                        onSuccess: () => {
                            this.moving = null;
                        },
                        onError: () => {
                            this.moveErrorDismissed = false;
                        },
                    },
                );
                return;
            }

            this.moving = null;

            // Wohnung: über die gemeinsame Reihenfolge ans Ende der Zielgruppe —
            // derselbe Weg wie beim Ziehen, damit die Folge lückenlos bleibt.
            this.arrangeInto(option.target, uuid);
        },

        requestDelete(node) {
            this.closeMenu();
            this.pendingDelete = node;
        },
        doDelete() {
            const node = this.pendingDelete;
            this.pendingDelete = null;
            if (!node) return;
            this.$inertia.delete(
                this.route("advisors.projects.buildings.destroy", {
                    project: this.project.uuid, building: node.uuid,
                }),
                { preserveScroll: true, preserveState: true },
            );
        },

        // Anordnen (gemeinsame Reihenfolge einer Ebene)
        arrange(scope, items) {
            this.$inertia.put(
                this.route("advisors.projects.arrange", { project: this.project.uuid }),
                { parent: scope, items: items.map((it) => ({ type: it.type, uuid: it.uuid })) },
                { preserveScroll: true, preserveState: true },
            );
        },
        arrangeRelative(parentScope, targetUuid, before) {
            const items = this.itemsOf(parentScope)
                .filter((it) => !(it.type === "apartment" && it.uuid === this.dragged.uuid));
            let idx = items.findIndex((it) => it.uuid === targetUuid);
            if (idx < 0) idx = items.length;
            items.splice(before ? idx : idx + 1, 0, { type: "apartment", uuid: this.dragged.uuid });
            this.arrange(parentScope, items);
        },
        // Hängt eine Wohnung ans Ende einer Ebene. Ohne uuid gilt die gerade
        // gezogene — der Verschieben-Dialog reicht sie dagegen mit.
        arrangeInto(scope, uuid = null) {
            const moved = uuid || this.dragged?.uuid;
            if (!moved) return;
            const items = this.itemsOf(scope)
                .filter((it) => !(it.type === "apartment" && it.uuid === moved));
            items.push({ type: "apartment", uuid: moved });
            this.arrange(scope, items);
        },

        // Drag-and-drop
        onDragStart(apartment, event) {
            this.dragged = apartment;
            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = "move";
                try { event.dataTransfer.setData("text/plain", apartment.uuid); } catch (e) { /* noop */ }
                const row = event.currentTarget.parentElement;
                if (row && event.dataTransfer.setDragImage) {
                    const r = row.getBoundingClientRect();
                    event.dataTransfer.setDragImage(row, event.clientX - r.left, event.clientY - r.top);
                }
            }
        },
        onDragEnd() {
            this.resetDrag();
        },
        resetDrag() {
            this.dragged = null;
            this.dragOver = { uuid: null, before: true };
            this.dragOverInto = null;
        },
        onItemOver(parentScope, uuid, event) {
            if (!this.dragged || this.dragged.uuid === uuid) return;
            event.preventDefault();
            this.dragOverInto = null;
            const r = event.currentTarget.getBoundingClientRect();
            this.dragOver = { uuid, before: (event.clientY - r.top) < r.height / 2 };
        },
        onItemDrop(parentScope, uuid, event) {
            if (!this.dragged || this.dragged.uuid === uuid) return;
            event.preventDefault();
            const r = event.currentTarget.getBoundingClientRect();
            const before = (event.clientY - r.top) < r.height / 2;
            this.arrangeRelative(parentScope, uuid, before);
            this.resetDrag();
        },
        onIntoOver(scope, event) {
            if (!this.dragged) return;
            event.preventDefault();
            this.dragOver = { uuid: null, before: true };
            this.dragOverInto = scope === null ? "__unassigned__" : scope;
        },
        onIntoDrop(scope, event) {
            if (!this.dragged) return;
            event.preventDefault();
            this.arrangeInto(scope);
            this.resetDrag();
        },
        dropClass(uuid) {
            if (this.dragOver.uuid !== uuid) return "";
            return this.dragOver.before ? "drop-top" : "drop-bot";
        },
    },
};
</script>
